import qrcode from 'qrcode-generator';
import { useMemo, useState, type FormEvent } from 'react';
import { useSessao } from '../autenticacao/ContextoDeSessao.js';
import {
  pedirCodigoDeRecuperacao,
  redefinirSenha,
  type Conclusao,
  type ResultadoDoLogin,
} from '../autenticacao/cognito.js';
import { CamposDeNovaSenha, problemaDaSenha } from '../componentes/CamposDeNovaSenha.js';

/**
 * Entrada do cockpit.
 *
 * O Cognito encadeia desafios, e a tela acompanha: credenciais → senha definitiva (no
 * primeiro acesso) → cadastro do autenticador → código. Em produção o MFA é obrigatório,
 * então o caminho completo é o normal, não a exceção.
 *
 * Em paralelo há o desvio de senha esquecida, que não é desafio do Cognito e sim outro
 * fluxo: pedir código por e-mail → redefinir → voltar para o login normal, inclusive com
 * o segundo fator, que a redefinição de senha não dispensa.
 */
type Etapa =
  | { nome: 'credenciais' }
  | {
      nome: 'nova-senha';
      exigeNome: boolean;
      concluir: (novaSenha: string, nome?: string) => Promise<ResultadoDoLogin>;
    }
  | { nome: 'cadastrar-mfa'; segredo: string; uri: string; concluir: Conclusao }
  | { nome: 'codigo-mfa'; concluir: Conclusao }
  | { nome: 'esqueci' }
  | { nome: 'redefinir'; destino: string };

/** O segredo é digitado à mão por quem não conseguir ler o QR. Em blocos, dá para copiar. */
function emBlocos(segredo: string): string {
  return (segredo.match(/.{1,4}/g) ?? []).join(' ');
}

export function Login() {
  const { entrar, confirmarEntrada } = useSessao();

  const [etapa, setEtapa] = useState<Etapa>({ nome: 'credenciais' });
  const [email, setEmail] = useState('');
  const [senha, setSenha] = useState('');
  const [nome, setNome] = useState('');
  const [novaSenha, setNovaSenha] = useState('');
  const [confirmacao, setConfirmacao] = useState('');
  const [codigo, setCodigo] = useState('');
  const [erro, setErro] = useState('');
  const [aviso, setAviso] = useState('');
  const [enviando, setEnviando] = useState(false);

  /** Um passo do fluxo pode abrir o próximo — quem entrou, entrou. */
  function avancar(resultado: ResultadoDoLogin) {
    switch (resultado.tipo) {
      case 'entrou':
        confirmarEntrada(resultado.operador);
        return;
      case 'nova-senha':
        setEtapa({
          nome: 'nova-senha',
          exigeNome: resultado.exigeNome,
          concluir: resultado.concluir,
        });
        return;
      case 'cadastrar-mfa':
        setEtapa({
          nome: 'cadastrar-mfa',
          segredo: resultado.segredo,
          uri: resultado.uri,
          concluir: resultado.concluir,
        });
        return;
      case 'codigo-mfa':
        setEtapa({ nome: 'codigo-mfa', concluir: resultado.concluir });
        return;
    }
  }

  async function executar(acao: () => Promise<ResultadoDoLogin>) {
    setErro('');
    setEnviando(true);
    try {
      avancar(await acao());
      setCodigo('');
    } catch (e) {
      setErro(e instanceof Error ? e.message : 'Não foi possível entrar.');
    } finally {
      setEnviando(false);
    }
  }

  function aoEntrar(evento: FormEvent) {
    evento.preventDefault();
    void executar(() => entrar(email.trim(), senha));
  }

  function aoTrocarSenha(evento: FormEvent) {
    evento.preventDefault();
    const problema = problemaDaSenha(novaSenha, confirmacao);
    if (problema) {
      setErro(problema);
      return;
    }
    if (etapa.nome !== 'nova-senha') return;
    const { concluir } = etapa;
    void executar(() => concluir(novaSenha, nome));
  }

  function aoEnviarCodigo(evento: FormEvent) {
    evento.preventDefault();
    if (etapa.nome !== 'cadastrar-mfa' && etapa.nome !== 'codigo-mfa') return;
    const { concluir } = etapa;
    void executar(() => concluir(codigo.trim()));
  }

  /** Fluxo de senha esquecida: não devolve `ResultadoDoLogin`, então tem o seu próprio. */
  async function executarSimples(acao: () => Promise<void>, aoDarCerto: () => void) {
    setErro('');
    setEnviando(true);
    try {
      await acao();
      aoDarCerto();
    } catch (e) {
      setErro(e instanceof Error ? e.message : 'Não foi possível concluir.');
    } finally {
      setEnviando(false);
    }
  }

  function aoPedirCodigo(evento: FormEvent) {
    evento.preventDefault();
    let destino = email.trim();
    void executarSimples(
      async () => {
        destino = await pedirCodigoDeRecuperacao(email.trim());
      },
      () => {
        setCodigo('');
        setNovaSenha('');
        setConfirmacao('');
        setEtapa({ nome: 'redefinir', destino });
      },
    );
  }

  function aoRedefinir(evento: FormEvent) {
    evento.preventDefault();
    const problema = problemaDaSenha(novaSenha, confirmacao);
    if (problema) {
      setErro(problema);
      return;
    }
    void executarSimples(
      () => redefinirSenha(email.trim(), codigo, novaSenha),
      () => {
        // Volta para o login em vez de entrar sozinho: a senha nova precisa ser usada uma
        // vez, e o segundo fator continua valendo — redefinir senha não dispensa o MFA.
        setSenha('');
        setCodigo('');
        setNovaSenha('');
        setConfirmacao('');
        setEtapa({ nome: 'credenciais' });
        setAviso('Senha redefinida. Entre com a nova senha.');
      },
    );
  }

  function voltarAoLogin() {
    setErro('');
    setAviso('');
    setEtapa({ nome: 'credenciais' });
  }

  return (
    <div className="tela-login">
      <div className="caixa-login">
        <div className="marca">A PONTE</div>
        <div className="sub">Cockpit de vendas · cadeiras</div>
        <div className="filete" />

        {etapa.nome === 'credenciais' && (
          <form onSubmit={aoEntrar}>
            <div className="campo">
              <label htmlFor="email">E-mail</label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="nome@rrevela.com.br"
                autoComplete="username"
                required
              />
            </div>
            <div className="campo">
              <label htmlFor="senha">Senha</label>
              <input
                id="senha"
                type="password"
                value={senha}
                onChange={(e) => setSenha(e.target.value)}
                autoComplete="current-password"
                required
              />
              <div className="dica">
                O acesso é nominal e registra quem emitiu cada documento e cada cobrança.
              </div>
            </div>
            <button className="bt cheio" type="submit" disabled={enviando}>
              {enviando ? 'Entrando…' : 'Entrar'}
            </button>
            <button
              type="button"
              className="bt-texto"
              onClick={() => {
                setErro('');
                setAviso('');
                setEtapa({ nome: 'esqueci' });
              }}
            >
              Esqueci minha senha
            </button>
          </form>
        )}

        {etapa.nome === 'esqueci' && (
          <form onSubmit={aoPedirCodigo}>
            <p className="dica" style={{ marginBottom: 18 }}>
              Informe o seu e-mail e enviaremos um código para você definir uma senha nova.
            </p>
            <div className="campo">
              <label htmlFor="email-recuperacao">E-mail</label>
              <input
                id="email-recuperacao"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="nome@rrevela.com.br"
                autoComplete="username"
                autoFocus
                required
              />
            </div>
            <button className="bt cheio" type="submit" disabled={enviando}>
              {enviando ? 'Enviando…' : 'Enviar código'}
            </button>
            <button type="button" className="bt-texto" onClick={voltarAoLogin}>
              Voltar para a entrada
            </button>
          </form>
        )}

        {etapa.nome === 'redefinir' && (
          <form onSubmit={aoRedefinir}>
            <p className="dica" style={{ marginBottom: 18 }}>
              Enviamos um código para <b>{etapa.destino}</b>. Ele vale por pouco tempo — se demorar,
              peça outro.
            </p>
            <div className="campo">
              <label htmlFor="codigo-email">Código recebido</label>
              <input
                id="codigo-email"
                inputMode="numeric"
                autoComplete="one-time-code"
                value={codigo}
                onChange={(e) => setCodigo(e.target.value.replace(/\s/g, ''))}
                style={{ letterSpacing: '.4em', fontSize: 18, textAlign: 'center' }}
                autoFocus
                required
              />
            </div>
            <CamposDeNovaSenha
              prefixo="redefinir"
              nova={novaSenha}
              confirmacao={confirmacao}
              aoMudarNova={setNovaSenha}
              aoMudarConfirmacao={setConfirmacao}
            />
            <button className="bt cheio" type="submit" disabled={enviando || !codigo}>
              {enviando ? 'Salvando…' : 'Redefinir senha'}
            </button>
            <button type="button" className="bt-texto" onClick={voltarAoLogin}>
              Voltar para a entrada
            </button>
          </form>
        )}

        {etapa.nome === 'nova-senha' && (
          <form onSubmit={aoTrocarSenha}>
            <p className="dica" style={{ marginBottom: 18 }}>
              Primeiro acesso. Defina a sua senha definitiva.
            </p>
            {etapa.exigeNome && (
              <div className="campo">
                <label htmlFor="nome">Nome completo</label>
                <input
                  id="nome"
                  type="text"
                  value={nome}
                  onChange={(e) => setNome(e.target.value)}
                  placeholder="Como deve aparecer na trilha de auditoria"
                  autoComplete="name"
                  required
                />
                <div className="dica">
                  É este nome que fica registrado em cada documento emitido e cada cobrança gerada.
                </div>
              </div>
            )}
            <CamposDeNovaSenha
              prefixo="primeiro-acesso"
              nova={novaSenha}
              confirmacao={confirmacao}
              aoMudarNova={setNovaSenha}
              aoMudarConfirmacao={setConfirmacao}
            />
            <button className="bt cheio" type="submit" disabled={enviando}>
              {enviando ? 'Salvando…' : 'Definir senha e continuar'}
            </button>
          </form>
        )}

        {etapa.nome === 'cadastrar-mfa' && (
          <CadastroDoAutenticador
            segredo={etapa.segredo}
            uri={etapa.uri}
            codigo={codigo}
            setCodigo={setCodigo}
            enviando={enviando}
            aoEnviar={aoEnviarCodigo}
          />
        )}

        {etapa.nome === 'codigo-mfa' && (
          <form onSubmit={aoEnviarCodigo}>
            <p className="dica" style={{ marginBottom: 18 }}>
              Digite o código de seis dígitos do seu aplicativo autenticador.
            </p>
            <CampoDeCodigo codigo={codigo} setCodigo={setCodigo} />
            <button className="bt cheio" type="submit" disabled={enviando || codigo.length < 6}>
              {enviando ? 'Conferindo…' : 'Entrar'}
            </button>
          </form>
        )}

        {erro && (
          <div className="aviso erro" style={{ marginTop: 16 }}>
            {erro}
          </div>
        )}

        {aviso && !erro && (
          <div className="aviso ok" style={{ marginTop: 16 }}>
            {aviso}
          </div>
        )}
      </div>
    </div>
  );
}

function CampoDeCodigo({ codigo, setCodigo }: { codigo: string; setCodigo: (v: string) => void }) {
  return (
    <div className="campo">
      <label htmlFor="codigo">Código do aplicativo</label>
      <input
        id="codigo"
        // `inputMode` numérico chama o teclado certo no celular sem recusar colar.
        inputMode="numeric"
        autoComplete="one-time-code"
        maxLength={6}
        value={codigo}
        onChange={(e) => setCodigo(e.target.value.replace(/\D/g, ''))}
        style={{ letterSpacing: '.5em', fontSize: 20, textAlign: 'center' }}
        autoFocus
        required
      />
    </div>
  );
}

/**
 * Cadastro do autenticador, uma vez por operador.
 *
 * O QR é gerado no próprio navegador — o segredo do MFA não pode viajar para um serviço
 * de terceiro para virar imagem. A CSP do cockpit também não deixaria: `img-src` só
 * aceita `self` e `data:`.
 */
function CadastroDoAutenticador({
  segredo,
  uri,
  codigo,
  setCodigo,
  enviando,
  aoEnviar,
}: {
  segredo: string;
  uri: string;
  codigo: string;
  setCodigo: (v: string) => void;
  enviando: boolean;
  aoEnviar: (evento: FormEvent) => void;
}) {
  const imagem = useMemo(() => {
    const qr = qrcode(0, 'M');
    qr.addData(uri);
    qr.make();
    return qr.createDataURL(5, 8);
  }, [uri]);

  return (
    <form onSubmit={aoEnviar}>
      <p className="dica" style={{ marginBottom: 14 }}>
        Este acesso exige um aplicativo autenticador (Google Authenticator, Authy, 1Password ou o
        gerenciador de senhas que você já usa). Leia o código abaixo e digite os seis dígitos que
        ele mostrar.
      </p>

      <div style={{ textAlign: 'center', marginBottom: 12 }}>
        <img
          src={imagem}
          alt="QR Code para cadastrar o aplicativo autenticador"
          style={{ width: 168, height: 168, background: '#fff', borderRadius: 6 }}
        />
      </div>

      <details style={{ marginBottom: 16 }}>
        <summary className="dica" style={{ cursor: 'pointer' }}>
          Não consegue ler o código?
        </summary>
        <p className="dica" style={{ marginTop: 8 }}>
          Cadastre manualmente com esta chave:
        </p>
        <code
          style={{
            display: 'block',
            wordBreak: 'break-all',
            fontSize: 13,
            letterSpacing: '.08em',
            marginTop: 4,
          }}
        >
          {emBlocos(segredo)}
        </code>
      </details>

      <CampoDeCodigo codigo={codigo} setCodigo={setCodigo} />

      <button className="bt cheio" type="submit" disabled={enviando || codigo.length < 6}>
        {enviando ? 'Conferindo…' : 'Cadastrar e entrar'}
      </button>

      <p className="dica" style={{ marginTop: 12 }}>
        Guarde o aplicativo: sem ele, só a operação consegue restabelecer o acesso.
      </p>
    </form>
  );
}
